import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Stack,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Payment } from '../../../../app/data/models/payments/payment';
import { Order } from '../../../../app/data/models/orders/order';
import { formatCurrency } from '../../../../app/utils/formatters';
import { format } from 'date-fns';
import PrintIcon from '@mui/icons-material/Print';
import RefundIcon from '@mui/icons-material/MoneyOff';
import { PaymentMethod } from '../../../../app/data/models/payments/paymentMethod';

interface PaymentDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedPayment: Payment | null;
  order: Order | null;
  processingAction: boolean;
  onPrintReceipt: (paymentId: string) => void;
  onOpenRefundDialog: (paymentId: string) => void;
  getPaymentMethodDisplay: (method: PaymentMethod) => string;
}

const PaymentDetailsDialog: React.FC<PaymentDetailsDialogProps> = ({
  open,
  onClose,
  selectedPayment,
  order,
  processingAction,
  onPrintReceipt,
  onOpenRefundDialog,
  getPaymentMethodDisplay,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!selectedPayment || !order) {
    return null;
  }

  // Filter refunds for this payment
  const refunds =
    order.refunds?.filter((r) => r.paymentId === selectedPayment.id) || [];

  // Calculate total refunded for this payment
  const totalRefunded = refunds.reduce((total, refund) => {
    return total + refund.amount;
  }, 0);

  // Check if payment is partially or fully refunded
  const isPartiallyRefunded =
    totalRefunded > 0 && totalRefunded < selectedPayment.amount;
  const isFullyRefunded = totalRefunded >= selectedPayment.amount;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('biller.payment-details')}</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  {formatCurrency(selectedPayment.amount)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 2 }}
                >
                  {getPaymentMethodDisplay(selectedPayment.paymentMethod)}
                </Typography>
                {isPartiallyRefunded && (
                  <Chip
                    size="small"
                    color="warning"
                    label={t('biller.partially-refunded')}
                    sx={{ ml: 1 }}
                  />
                )}
                {isFullyRefunded && (
                  <Chip
                    size="small"
                    color="error"
                    label={t('biller.already-refunded')}
                    sx={{ ml: 1 }}
                  />
                )}
                {totalRefunded > 0 && (
                  <Typography variant="body2" color="error.main" sx={{ ml: 2 }}>
                    {t('biller.total-refunded')}:{' '}
                    {formatCurrency(totalRefunded ?? 0)}
                  </Typography>
                )}
              </Box>
              <Stack spacing={2}>
                {refunds.map((refund) => (
                  <Paper
                    key={refund.id}
                    variant="outlined"
                    sx={{ p: 1.5, background: '#fafafa' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="error.main"
                        fontWeight="medium"
                      >
                        {formatCurrency(refund.amount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {refund.refundDate &&
                          format(new Date(refund.refundDate), 'PPp')}
                      </Typography>
                    </Box>
                    {refund.reason && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {t('biller.reason')}: {refund.reason}
                      </Typography>
                    )}
                    {refund.processedByUserName && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {t('biller.processed-by')}: {refund.processedByUserName}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={() => onPrintReceipt(selectedPayment.id)}
              startIcon={<PrintIcon />}
              sx={{ mb: isMobile ? 1 : 0 }}
            >
              {t('biller.print-receipt')}
            </Button>
            {selectedPayment.isPaid && (
              <Button
                color="secondary"
                onClick={() => onOpenRefundDialog(selectedPayment.id)}
                startIcon={<RefundIcon />}
                disabled={
                  processingAction ||
                  Boolean(
                    order.refunds?.some(
                      (r) =>
                        r.paymentId === selectedPayment.id &&
                        r.amount >= selectedPayment.amount
                    )
                  )
                }
              >
                {t('biller.refund')}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDetailsDialog;
